import { useState } from 'react';
import { Link } from 'react-router-dom';
import { standings } from '@shared/game-logic';
import { Empty, Panel, Screen } from '@/components/ui';
import Avatar from '@/components/Avatar';
import { formatDateTime, seatColor, signed } from '@/lib/labels';
import { useLeaderboard } from '@/lib/useLeaderboard';
import { selectGameList, useScorekeeper } from '@/store/scorekeeper';
import { selectKlaverjasList, useKlaverjasScore } from '@/store/klaverjasScore';
import { klaverjasTotals } from '@shared/types/klaverjas';
import DeleteButton from '@/components/DeleteButton';
import { removeGame } from '@/lib/removeGame';

type Tab = 'ranglijst' | 'potjes';

export default function History() {
  const [tab, setTab] = useState<Tab>('ranglijst');
  const games = useScorekeeper(selectGameList);
  const klaverjas = useKlaverjasScore(selectKlaverjasList);
  const pendingSync = useScorekeeper((state) => state.pendingSync);
  const [error, setError] = useState<string | null>(null);

  /*
   * Both games, newest first. Klaverjassen was missing here entirely, which
   * mattered the moment there was something to delete: a potje you cannot see is
   * one you cannot throw away.
   */
  const finished = [
    ...games
      .filter((game) => game.status === 'finished')
      .map((game) => ({
        id: game.id,
        kind: 'boerenbridge' as const,
        at: game.finishedAt ?? game.updatedAt,
        to: `/score/${game.id}`,
        rows: standings(game.rounds, game.seats).map((row) => ({
          seat: row.seat,
          name: row.displayName,
          total: signed(row.total),
          placement: row.placement,
        })),
      })),
    ...klaverjas
      .filter((game) => game.status === 'finished')
      .map((game) => {
        const totals = klaverjasTotals(game);
        return {
          id: game.id,
          kind: 'klaverjas' as const,
          at: game.finishedAt ?? game.updatedAt,
          to: `/klaverjas/${game.id}`,
          rows: [0, 1].map((team) => ({
            seat: team,
            name: `${game.seats[team]?.displayName} & ${game.seats[team + 2]?.displayName}`,
            total: String(totals[team]),
            placement: totals[team] >= totals[1 - team] ? 1 : 2,
          })),
        };
      }),
  ].sort((a, b) => b.at.localeCompare(a.at));

  return (
    <Screen title="Historie" back="/">
      <nav className="flex gap-1 rounded-xl bg-base-800/70 p-1">
        {(['ranglijst', 'potjes'] as Tab[]).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setTab(option)}
            aria-current={tab === option}
            className={[
              'min-h-10 flex-1 rounded-lg text-sm font-medium capitalize transition-colors',
              tab === option ? 'bg-base-600 text-white' : 'text-slate-400 hover:text-white',
            ].join(' ')}
          >
            {option}
          </button>
        ))}
      </nav>

      {tab === 'ranglijst' ? <Leaderboard pending={pendingSync.length} /> : null}

      {tab === 'potjes' && (
        <>
          {finished.length === 0 && (
            <Empty>Nog geen afgerond potje. Speel er een uit en hij komt hier te staan.</Empty>
          )}
          {finished.map((game) => (
            <Panel
              key={game.id}
              title={formatDateTime(game.at)}
              action={
                <span className="flex items-center gap-3">
                  <Link to={game.to} className="text-sm text-accent-400 hover:underline">
                    Bekijk
                  </Link>
                  <DeleteButton
                    label="weggooien"
                    onDelete={async () => {
                      setError(null);
                      try {
                        await removeGame(game.id, game.kind);
                      } catch (err) {
                        setError(err instanceof Error ? err.message : 'Weggooien mislukt.');
                      }
                    }}
                  />
                </span>
              }
            >
              <ol className="space-y-1.5">
                {game.rows.map((row) => (
                  <li key={row.seat} className="flex items-center gap-3">
                    <span className="w-5 text-sm tabular-nums text-slate-500">
                      {row.placement}.
                    </span>
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: seatColor(row.seat) }}
                    />
                    <span className="min-w-0 flex-1 truncate">{row.name}</span>
                    <span className="font-semibold tabular-nums">{row.total}</span>
                  </li>
                ))}
              </ol>
            </Panel>
          ))}

          {error && (
            <p role="alert" className="text-sm text-bad">
              {error}
            </p>
          )}
        </>
      )}
    </Screen>
  );
}

/**
 * All-time standings across every device that has synced. Both the scorekeeper
 * and the live game feed this, so it is the one place that answers "who is
 * actually the best?".
 */
function Leaderboard({ pending }: { pending: number }) {
  const state = useLeaderboard();

  if (state.status === 'loading') {
    return (
      <Panel title="Aller tijden">
        <p className="text-sm text-slate-400">Laden…</p>
      </Panel>
    );
  }

  if (state.status === 'unavailable') {
    return (
      <Panel title="Aller tijden">
        <p className="text-sm text-slate-400">
          Geen verbinding met de server, dus de ranglijst over alle toestellen is nu niet op te
          halen. De potjes op dit toestel staan onder het tabblad Potjes.
        </p>
        {pending > 0 && (
          <p className="mt-2 text-sm text-slate-500">
            {pending} {pending === 1 ? 'potje wacht' : 'potjes wachten'} op verzending; dat gebeurt
            vanzelf zodra er weer verbinding is.
          </p>
        )}
      </Panel>
    );
  }

  if (state.rows.length === 0) {
    return (
      <Panel title="Aller tijden">
        <Empty>Nog geen afgerond potje gesynchroniseerd.</Empty>
      </Panel>
    );
  }

  return (
    <>
      <Panel title="Aller tijden">
        <ol className="space-y-3">
          {state.rows.map((row, index) => (
            <li key={row.key}>
              <div className="flex items-center gap-3">
                <span className="w-5 text-sm tabular-nums text-slate-500">{index + 1}.</span>
                <Avatar
                  player={{ name: row.name, emoji: row.emoji ?? undefined, avatar: row.avatar }}
                  size={36}
                />
                <span className="min-w-0 flex-1 truncate font-medium">{row.name}</span>
                <span className="text-sm text-slate-400">
                  {row.gamesWon}/{row.gamesPlayed} gewonnen
                </span>
              </div>
              <p className="mt-0.5 pl-[4.25rem] text-xs text-slate-500">
                {Math.round(row.exactPct)}% precies goed · gemiddeld {Math.round(row.avgFinalScore)}{' '}
                punten
                {row.bestFinalScore !== null && ` · beste potje ${row.bestFinalScore}`}
                {' · '}
                {describeBidder(row.overUnderIndex)}
              </p>
            </li>
          ))}
        </ol>
      </Panel>
      {pending > 0 && (
        <p className="text-center text-sm text-slate-500">
          {pending} {pending === 1 ? 'potje wacht' : 'potjes wachten'} nog op verzending.
        </p>
      )}
    </>
  );
}

function describeBidder(index: number): string {
  const rounded = Math.round(index * 10) / 10;
  if (Math.abs(rounded) < 0.15) return 'biedt zuiver';
  return rounded > 0 ? `overbiedt (+${rounded.toFixed(1)})` : `onderbiedt (${rounded.toFixed(1)})`;
}
